import { useEffect, useRef, useState } from 'preact/hooks';
import { config, videoState, dhVideoAspect } from '../store/widget-store';
import { resolveDigitalPosterUrl } from '../services/oss-poster';
import { useI18n } from '../hooks/use-i18n';
import type { VideoState } from '../services/video-state-machine';

/**
 * 数字人视频区 — 双 <video> cross-fade。
 *
 * 切换策略:
 *  - 维护两个 <video>: A / B,active=当前可见
 *  - videoState 变更时,把新 src 设到隐藏的那一侧,等 'canplay' 触发后切 active,
 *    旧 video opacity:1 -> 0,新 video 0 -> 1 (CSS 200ms transition)
 *  - 所有 video 设 muted/loop/playsInline/autoplay/preload="auto"
 *
 * 视觉降级 (开放问题 #2 临时实现):若缺少对应 state 视频,优先用 idle,
 * 再退到第一个可用素材。
 *
 * 分段循环窗口(数字人成片 = 0~16s 闭嘴静默段 + 16~32s 张嘴口播段):
 *  - 非 speaking(idle/listening):只循环 0 ~ IDLE_LOOP_END_SEC 秒静默段,数字人闭嘴待机。
 *  - speaking(音频播放中):接管循环口播段 [SPEAK_START_SEC, SPEAK_END_SEC]=[16,32],模拟开口口播;
 *    动态关原生 loop(防其播到结尾自动回 0 闪一帧静默段),用 timeupdate 主防线 + ended 兜底
 *    回跳 SPEAK_START_SEC;进入 speaking 立即跳到口播起点,口型零延迟。
 * idle 上界 15、口播下界 16,中间 1s 作过渡缓冲,两段各避开张嘴/闭嘴的过渡帧。
 * 口播上界固定 32s(duration<32 时回退 duration 兜底)。JSX 静态 loop 保留作初始/降级兜底。
 */

/** 待机段循环上界(秒):视频源 0~15s 视为闭嘴静默片段,无语音时只循环这一段(<16 留过渡缓冲) */
const IDLE_LOOP_END_SEC = 15;
/** 口播段起点(秒):后端成片约定 16s 前为静默,16s 后为张嘴口播,speaking 时循环 [SPEAK_START_SEC, SPEAK_END_SEC] */
const SPEAK_START_SEC = 16;
/** 口播段上界(秒):固定 32s;视频 duration<32 时回退到 duration,避免 seek 越界 */
const SPEAK_END_SEC = 32;
/**
 * 接近上界提前回跳的余量(秒)。timeupdate ~4/s,在播到上界(原生会自动回 0)之前
 * 先回跳口播起点,避免闪一帧静默段。取 0.25s ≈ 一个 timeupdate 间隔 + 余量。
 */
const SPEAK_END_BUFFER_SEC = 0.25;

interface AssetMap {
  idle?: string;
  listening?: string;
  speaking?: string;
  greeting?: string;
}

function buildAssetMap(): AssetMap {
  const out: AssetMap = {};
  for (const a of config.value?.digitalHumanAssets ?? []) {
    if (!out[a.state as keyof AssetMap]) {
      out[a.state as keyof AssetMap] = a.videoUrl;
    }
  }
  return out;
}

function fallback(map: AssetMap, state: VideoState): string | undefined {
  return map[state] ?? map.idle ?? Object.values(map)[0];
}

export function DigitalHumanVideo() {
  const cfg = config.value;
  const state = videoState.value;
  const { t } = useI18n();
  const [active, setActive] = useState<'A' | 'B'>('A');
  const refA = useRef<HTMLVideoElement>(null);
  const refB = useRef<HTMLVideoElement>(null);
  const [srcA, setSrcA] = useState<string | undefined>(undefined);
  const [srcB, setSrcB] = useState<string | undefined>(undefined);
  // 视频加载失败(如签名 URL 过期 403 / 资源损坏)置位,落到兜底;素材集变化时复位
  const [loadFailed, setLoadFailed] = useState(false);
  // 海报图本身加载失败(ACL 收紧等)置位收起,不挡视频区
  const [posterFailed, setPosterFailed] = useState(false);
  // 视频出帧标记(playing 已触发)。小程序端撤海报的唯一依据:绝不能在手势到达时撤——
  // 手势回调里 load()+play() 只是"开始加载",首帧渲染还有一段网络+解码时间,
  // 提前撤海报会露出 video 透明底,肉眼可见白屏闪现。
  const [firstFrameReady, setFirstFrameReady] = useState(false);

  // 手势解锁兜底:小程序 web-view 里 shadow 内 <video> 不自动 preload/不自动播放
  // (networkState 恒 0),任一用户手势中调 load()+play() 即正常播放;普通浏览器靠
  // autoplay muted 自行起播,此监听命中时是 no-op(readyState 非 0 不 load、非 paused
  // 不 play),同时兜住 Safari 低电量模式等 muted autoplay 被拦的极端场景。
  // 在 document 挂 capture 监听,等价给 widget 里所有按钮/触控区统一加一遍解锁入口。
  useEffect(() => {
    const unlock = () => {
      for (const v of [refA.current, refB.current]) {
        if (!v) continue;
        try {
          // load() 触发被压制的预加载;paused 校正未起来的自动播放
          if (v.readyState === 0) v.load();
          if (v.paused) void v.play().catch(() => {});
        } catch {
          /* readyState 非 0 时 load 会重启播放,已用条件挡住;其它异常不冒泡 */
        }
      }
    };
    document.addEventListener('pointerdown', unlock, true);
    return () => document.removeEventListener('pointerdown', unlock, true);
    // 与视频生命周期同级,挂一次即可
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 初次挂载时给 A 装上 idle 资源,无 cross-fade
  useEffect(() => {
    const map = buildAssetMap();
    const url = fallback(map, 'idle');
    setSrcA(url);
    setActive('A');
    // 仅挂载阶段一次性
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 素材集变化(新视频就位)时,清除上一轮的加载失败标记,给恢复机会
  useEffect(() => {
    setLoadFailed(false);
    setPosterFailed(false);
  }, [cfg]);

  // videoState 变化:把新 src 装到隐藏的那侧,canplay 后切 active
  useEffect(() => {
    if (!cfg) return;
    const map = buildAssetMap();
    const url = fallback(map, state);
    if (!url) return;

    // 当前激活那一面已经是这个 url,跳过
    const currentSrc = active === 'A' ? srcA : srcB;
    if (currentSrc === url) return;

    if (active === 'A') {
      setSrcB(url);
      const v = refB.current;
      if (!v) return;
      const onCan = () => {
        setActive('B');
        v.removeEventListener('canplay', onCan);
        v.removeEventListener('loadeddata', onCan);
      };
      v.addEventListener('canplay', onCan);
      v.addEventListener('loadeddata', onCan);
      // jsdom 没有 canplay 事件,直接切 (覆盖单测路径)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (typeof (v as any).canPlayType !== 'function') {
        setActive('B');
      }
    } else {
      setSrcA(url);
      const v = refA.current;
      if (!v) return;
      const onCan = () => {
        setActive('A');
        v.removeEventListener('canplay', onCan);
        v.removeEventListener('loadeddata', onCan);
      };
      v.addEventListener('canplay', onCan);
      v.addEventListener('loadeddata', onCan);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (typeof (v as any).canPlayType !== 'function') {
        setActive('A');
      }
    }
    // 仅在 state 改变时跑;active/src 是受控副作用,不放进依赖以避免循环
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, cfg]);

  // 分段循环钳制:作用在当前可见(active)那侧 video。state/active 变化时重绑。
  //  - speaking:循环口播段 [SPEAK_START_SEC, duration];
  //  - idle/listening:循环静默段 [0, IDLE_LOOP_END_SEC]。
  useEffect(() => {
    const v = active === 'A' ? refA.current : refB.current;
    if (!v) return;

    if (state === 'speaking') {
      // 口播段循环:JS 接管,关原生 loop 防其播到结尾回 0 闪静默段
      const speakClamp = () => {
        const d = v.duration;
        // metadata 未就绪(NaN/0):等 loadedmetadata 后下一帧再处理
        if (!Number.isFinite(d) || d <= 0) return;
        // 成片无口播段(太短):降级整段原生 loop
        if (d <= SPEAK_START_SEC) {
          v.loop = true;
          return;
        }
        v.loop = false;
        // 口播段上界固定 32s;视频比 32 短则回退 duration,避免 seek 越界
        const upper = Math.min(SPEAK_END_SEC, d);
        // 在静默段 / 越过上界余量 → 回跳口播起点
        if (v.currentTime < SPEAK_START_SEC || v.currentTime >= upper - SPEAK_END_BUFFER_SEC) {
          try {
            v.currentTime = SPEAK_START_SEC;
            if (v.paused) void v.play().catch(() => {});
          } catch {
            /* src 未就绪时设 currentTime 可能抛,忽略 */
          }
        }
      };
      // 兜底:timeupdate 余量没兜住(后台标签节流等)时,ended 强制回跳续播
      const onEnded = () => {
        try {
          v.currentTime = SPEAK_START_SEC;
          void v.play().catch(() => {});
        } catch {
          /* ignore */
        }
      };
      const onMeta = () => speakClamp(); // metadata 到位即起口型,避免开头停在静默段

      speakClamp(); // 进入即同步一次:若还在静默段立刻跳口播起点
      v.addEventListener('timeupdate', speakClamp);
      v.addEventListener('ended', onEnded);
      v.addEventListener('loadedmetadata', onMeta);
      return () => {
        v.removeEventListener('timeupdate', speakClamp);
        v.removeEventListener('ended', onEnded);
        v.removeEventListener('loadedmetadata', onMeta);
        v.loop = true; // 还原:切回 idle / 切 video 时让原生 loop 兜底
      };
    }

    // idle/listening:静默段循环
    const idleClamp = () => {
      if (v.currentTime >= IDLE_LOOP_END_SEC) {
        try {
          v.currentTime = 0;
        } catch {
          /* src 未就绪时设 currentTime 可能抛,忽略 */
        }
      }
    };
    // 刚回到待机时,若说话段已播过 15s,立即拉回待机段起点
    idleClamp();
    v.addEventListener('timeupdate', idleClamp);
    return () => v.removeEventListener('timeupdate', idleClamp);
  }, [state, active]);

  // 视频元数据就绪即上报真实宽高比,供 .dcs-dh-stage 收窄左框贴合视频(消除两侧留白);
  // idle/listening/speaking 多素材比例一致,任一侧上报即可,写同值无副作用。
  const onMeta = (e: Event) => {
    const v = e.currentTarget as HTMLVideoElement;
    if (v.videoWidth > 0 && v.videoHeight > 0) {
      dhVideoAspect.value = v.videoWidth / v.videoHeight;
    }
  };

  if (!cfg || cfg.type !== 'digital') return null;

  // 无任何可用素材(视频没生成成功 / 未就绪)或加载报错 → 落兜底,避免左侧纯黑屏
  const hasAsset = fallback(buildAssetMap(), 'idle') !== undefined;
  const showFallback = !hasAsset || loadFailed;

  // 加载期海报(全平台):视频出帧前用首帧图占住视频区,避免加载空白。海报 URL 优先
  // 后端回填的 posterUrl;未回填(当前常态)时前端从 OSS 视频 URL 派生小尺寸截帧
  // (t_0 截帧 = 视频首帧)。海报与视频首帧同画面、同盒模型同 object-fit,
  // 必须等 playing(firstFrameReady)才撤——出帧瞬间硬切无缝,提前撤会露出透明底白屏。
  // 小程序端靠手势解锁 load()+play() 起播,其它平台走 muted autoplay,殊途同归。
  // 与启动预载(poster-preload)共用同一解析:两路 URL 逐字节一致,浏览器缓存才能命中。
  const posterUrl = resolveDigitalPosterUrl(cfg);
  const showPoster = !firstFrameReady && !!posterUrl && !posterFailed && !showFallback;

  return (
    <div class="dcs-dh-video" data-state={state} aria-hidden="true">
      <video
        ref={refA}
        src={srcA}
        autoplay
        loop
        muted
        playsInline
        preload="auto"
        onLoadedMetadata={onMeta}
        onPlaying={() => setFirstFrameReady(true)}
        onError={() => setLoadFailed(true)}
        class={`dcs-dh-video__el ${active === 'A' ? 'is-active' : ''}`}
      />
      <video
        ref={refB}
        src={srcB}
        autoplay
        loop
        muted
        playsInline
        preload="auto"
        onLoadedMetadata={onMeta}
        onPlaying={() => setFirstFrameReady(true)}
        onError={() => setLoadFailed(true)}
        class={`dcs-dh-video__el ${active === 'B' ? 'is-active' : ''}`}
      />
      {showPoster && (
        <img
          class="dcs-dh-video__poster"
          src={posterUrl!}
          alt=""
          onLoad={(e) => {
            // 海报是视频首帧的等比缩放(w_480),naturalWidth/naturalHeight 即视频精确宽高比:
            // 海报加载完成(几 KB,远早于视频元数据)即收窄 stage 贴合,海报期两侧不留白;
            // 视频元数据到后 onMeta 写同值,无跳变。
            const img = e.currentTarget as HTMLImageElement;
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
              dhVideoAspect.value = img.naturalWidth / img.naturalHeight;
            }
          }}
          onError={() => setPosterFailed(true)}
        />
      )}
      {showFallback && (
        <div class="dcs-dh-fallback">
          {cfg.avatarUrl ? (
            <img class="dcs-dh-fallback__avatar" src={cfg.avatarUrl} alt="" />
          ) : (
            <span class="dcs-dh-fallback__icon" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                width="48"
                height="48"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </span>
          )}
          <p class="dcs-dh-fallback__text">{t('panel.videoPreparing')}</p>
        </div>
      )}
    </div>
  );
}
